import type { UseQueryResult } from '@tanstack/react-query'
import { useQueries } from '@tanstack/react-query'
import { useCallback } from 'react'

import { controllerByEquipmentNumberQueryOptions } from '@/features/controller/hooks/useControllerByEquipmentNumberQuery'
import { controllerDescriptionQueryOptions } from '@/features/controller/hooks/useControllerDescriptionQuery'
import type { ControllerDescription, ControllerListIdentifier } from '@/features/controller/types'

export type ControllerListRow = {
  identifier: ControllerListIdentifier
  query: UseQueryResult<ControllerDescription | null, Error>
}

function controllerQueryOptions({ equipmentNumber, id }: ControllerListIdentifier) {
  const getControllerOptions = { skipGlobalForbiddenErrorInterceptor: true }
  return id
    ? controllerDescriptionQueryOptions(id, getControllerOptions)
    : controllerByEquipmentNumberQueryOptions(equipmentNumber!, getControllerOptions)
}

export function useControllerListRows(controllerListIdentifiers: ControllerListIdentifier[]) {
  // The results come in the order of the queries, so result `index` belongs to identifier `index`.
  // Stable until the identifiers change, so that useQueries only rebuilds the rows when a result changes.
  const combine = useCallback(
    (queries: UseQueryResult<ControllerDescription | null, Error>[]): ControllerListRow[] =>
      controllerListIdentifiers.map((identifier, index) => ({ identifier, query: queries[index] })),
    [controllerListIdentifiers],
  )

  return useQueries({ queries: controllerListIdentifiers.map(controllerQueryOptions), combine })
}
